/** @jsx jsx */
import { jsx } from 'theme-ui'
import { Label, Field, Button } from '../../../atoms'
import React, { Fragment, useEffect, useState } from 'react'
import ProgressCircle from '../../form/progress-circle'
import classNames from 'classnames'
import { FsCore } from '../../../types/fs-core'
import { merge, isEmpty } from 'lodash-es'
import dayjs from '../../../utils/date'
import { useComponentTracking } from '../../../utils/component-events'
import AddressInput, { addressFieldNames, getAddressErrors } from '../../address-input'
import { addressbaseLookup } from '../../address-lookup/fetch'

interface BusinessDetails {
  businessName?: string
  companyNumber?: string
  address?: any
  email?: string
  businessType?: string
  children?: React.ReactNode
  numberOfEmployees?: number
  turnover?: number
  companyType?: string
}

const editModeFields = {
  businessName: {
    label: 'Name of business (this might be your own name)',
    validation: {
      required: { value: true, message: 'Please enter your business name' },
      maxLength: {
        value: 50,
        message: 'Maximum 50 character business name',
      },
      minLength: {
        value: 3,
        message: 'Minimum 3 character business name',
      },
      pattern: {
        value: /^[a-zA-Z0-9 /\-&.,'()]+$/,
      },
    },
  },
  companyNumber: {
    label: 'Company Number',
    validation: {
      required: { value: true, message: 'Please enter your company number' },
    },
  },
}

const EditMode = ({
  businessDetails,
  registerWithTrack,
  setValue,
  setError,
  clearErrors,
  errors,
  soleTrader,
  register,
  companyPostcode,
}) => {
  let fields = Object.keys(editModeFields)

  const addressFields = addressFieldNames.reduce((o, f) => {
    return addressFieldNames.includes(f)
      ? {
          ...o,
          [f]: businessDetails.address?.[f],
        }
      : o
  }, {})

  fields = fields.filter((f) => !addressFieldNames.includes(f))

  if (soleTrader) {
    fields = fields.filter((f) => f !== 'companyNumber')
  }

  return (
    <div className='SMEform__companyDetails__detailsBox'>
      {fields.map((field, index) => {
        return (
          <Field key={index} stack={true}>
            <Label htmlFor={field} className={field === 'companyNumber' ? 'disabled' : 'required'}>
              {editModeFields[field].label}
            </Label>
            <input
              type='text'
              id={field}
              name={`businessDetails.${field}`}
              value={businessDetails?.[field] || ''}
              readOnly={field === 'companyNumber'}
              onChange={(e) => {
                setValue(`businessDetails.${field}`, e.target.value)
              }}
              ref={registerWithTrack(
                {
                  ...editModeFields[field].validation,
                  ...(soleTrader && field === 'businessName'
                    ? {
                        pattern: {
                          value: /^(?!.*(ltd|limited)).*$/i,
                          message: 'Business Name cannot include Ltd or Limited',
                        },
                      }
                    : {}),
                },
                field,
              )}
            />

            {errors?.businessDetails?.[field] && (
              <div className='SMEform__error'>{errors.businessDetails[field].message}</div>
            )}
          </Field>
        )
      })}
      <AddressInput
        name='businessDetails.address'
        onChange={(address) => {
          if (address && typeof address === 'object') {
            register('businessDetails.address')
            setValue('businessDetails.address', address)
            const newErrors = getAddressErrors(address)
            for (const [field, error] of Object.entries(newErrors)) {
              if (error) {
                setError(`businessDetails.address.${field}`, {
                  type: 'manual',
                  message: error,
                })
              }
            }
            const oldBusinessDetailsAddressErrors = errors?.businessDetails?.address
            if (!oldBusinessDetailsAddressErrors) {
              return
            }
            for (const [field] of Object.entries(oldBusinessDetailsAddressErrors)) {
              if (!newErrors[field]) {
                clearErrors(`businessDetails.address.${field}`)
              }
            }
          }
        }}
        lookup={addressbaseLookup}
        errors={errors?.businessDetails?.address}
        formVariant='default'
        soleTrader={soleTrader}
        defaultAddressView={isEmpty(businessDetails.address) ? 'postcode' : 'static'}
        defaultFormValues={addressFields}
        postcode={companyPostcode ?? businessDetails?.address?.postcode}
      />
    </div>
  )
}

const BusinessFinder = ({
  businessDetails,
  brand,
  disable = false,
  registerWithTrack,
  register,
  setValue,
  setError,
  clearErrors,
  errors,
  soleTrader = false,
  companyFilter,
  unregister,
  businessNameError,
  defaultBusinessNameInput = null,
}: {
  businessDetails: {} | BusinessDetails
  brand: FsCore.FSBrand
  companyFilter?: string[]
}) => {
  const { __FS_API_URL__ } = global
  const apiPath: string = __FS_API_URL__
  const trackComponent = useComponentTracking()
  const findBusiness = (e) => {
    setValue('businessDetails', { businessName: undefined })
    setLoading(true)
    e.preventDefault()

    if (!businessNameInput) {
      setBusinessFinderError('Please fill in your business name')
      setLoading(false)
      return
    }

    trackComponent('Form Event', 'formFieldInteracted', {
      formStep: 'find_business_button_clicked',
      businessName: businessNameInput,
    })

    fetch(`${apiPath}/company/${businessNameInput}`)
      .then((response) => response.json())
      .then((response: { data: BusinessDetails[] }) => {
        setLoading(false)
        if (response.data.length < 1) {
          setBusinessFinderError("We couldn't find any results for that name")
          setCompanies(response.data)
        } else {
          setBusinessFinderError(undefined)
          if (companyFilter) {
            const filteredCompanies = response.data.filter((d) =>
              companyFilter.includes(d.companyType),
            )
            setCompanies(filteredCompanies)
          } else {
            setCompanies(response.data)
          }
        }
      })
      .catch(() => {
        setBusinessFinderError('oops, something went wrong. please try again later')
        setLoading(false)
      })
  }
  const [businessNameInput, setBusinessNameInput] = useState<null | string>(null)
  const [companies, setCompanies] = useState<null | BusinessDetails[]>(null)
  const [companyPostcode, setCompanyPostCode] = useState<any>(null)
  const [businessFinderError, setBusinessFinderError] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setBusinessNameInput(defaultBusinessNameInput)
  }, [defaultBusinessNameInput])

  useEffect(() => {
    if (soleTrader) {
      unregister('businessNameInput')
      setCompanies([])
    }
  }, [soleTrader])

  useEffect(() => {
    if (businessDetails.address) {
      setValue('businessDetails.addressSnippet', businessDetails.address?.summaryline)
    }
  }, [companyPostcode, businessDetails.address])

  return (
    <Field
      className={classNames('SMEform__companyDetails', {
        'SMEform__companyDetails--disable': disable,
      })}
    >
      <Label htmlFor='businessName'>Enter your business details</Label>
      <div className='SMEform__input SMEform__companyDetails__businessSearchBox'>
        {soleTrader ? (
          <EditMode
            businessDetails={businessDetails}
            registerWithTrack={registerWithTrack}
            register={register}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            soleTrader={true}
          />
        ) : (
          <Fragment>
            <div>We use Companies House data to search your company&apos;s details.</div>
            <input
              type='text'
              id='businessName'
              placeholder='Business name'
              name='businessNameInput'
              onChange={(e) => {
                setBusinessNameInput(e.target.value)
                setValue('businessNameInput', e.target.value)
              }}
              onKeyUp={(e) => {
                setBusinessFinderError(undefined)
                if (e.key === 'Enter' || e.keyCode === 13) {
                  findBusiness(e)
                }
              }}
              ref={registerWithTrack(
                { required: disable ? false : !soleTrader },
                'businessNameInput',
              )}
            />
            {errors?.businessNameInput && !businessFinderError && (
              <div className='SMEform__companyDetails__error'>
                Please enter your company details
              </div>
            )}
            {businessFinderError && (
              <div className='SMEform__companyDetails__error'>{businessFinderError}</div>
            )}
            <div className='SMEform__companyDetails__cta'>
              <Button
                variant={brand === 'money' ? 'secondary' : 'tertiary'}
                size='md'
                onClick={(e) => {
                  findBusiness(e)
                }}
              >
                Find business
              </Button>
              {loading && <ProgressCircle height={48} />}
            </div>

            {companies && companies.length > 0 && (
              <select
                onChange={async (e) => {
                  const company = companies.find((c) => c.businessName === e.target.value)
                  if (company) {
                    const res = await fetch(`${apiPath}/company/${company.companyNumber}/details`)
                    if (!res.ok) {
                      setBusinessFinderError('oops something went wrong please try again')
                    }
                    const companyDetails = await res.json()
                    const flatCompany = merge(company, companyDetails.data)

                    Object.keys(flatCompany).forEach((k) => {
                      if (k === 'date_of_creation') {
                        const nowDate = dayjs()
                        const existenceYears = nowDate.diff(dayjs(flatCompany[k]), 'year')
                        const existenceYearsString =
                          existenceYears > 4 ? '5+' : existenceYears.toString()
                        register('businessDetails.existenceYears')
                        setValue('businessDetails.existenceYears', existenceYearsString)
                        register('businessDetails.date_of_creation')
                        setValue('businessDetails.date_of_creation', flatCompany[k])
                        register('businessDetails.dateOfCreation')
                        setValue('businessDetails.dateOfCreation', flatCompany[k])
                      } else if (k !== 'companiesHouseAddress') {
                        register(`businessDetails.${k}`)
                        setValue(`businessDetails.${k}`, flatCompany[k])
                      }
                    })
                    setCompanyPostCode(flatCompany.companiesHouseAddress.postal_code)
                    setValue(`businessDetails.address`, {})
                  }
                }}
                name='businesses'
                value={businessDetails.businessName ?? ''}
              >
                <option>Please select your business</option>
                {companies.map((c, index) => (
                  <option key={index}>{c.businessName}</option>
                ))}
              </select>
            )}
            {businessDetails.businessName && (
              <div className='SMEform__companyDetails__detailsBox'>
                <EditMode
                  businessDetails={businessDetails}
                  registerWithTrack={registerWithTrack}
                  register={register}
                  setValue={setValue}
                  setError={setError}
                  clearErrors={clearErrors}
                  errors={errors}
                  soleTrader={false}
                  companyPostcode={companyPostcode}
                />
              </div>
            )}
          </Fragment>
        )}
      </div>
      {businessNameError && <div className='SMEform__error'>{businessNameError}</div>}
    </Field>
  )
}

export default BusinessFinder
